import { flow } from 'fp-ts/function';
import { at, get, isNil } from 'lodash/fp';
import { createSelector } from 'reselect';
import { getNumberProp, getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender } from '@mews/framework';
import { TimeUnit } from '@mews/enums';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { areAllNil, createOptionsSelector } from '@mews-commander/utils';
import { formValuesSelector } from '@mews-commander/legacy-form';
import { getExchangeRates } from '@mews-commander/legacy-entities/src/ducks/exchangeRates';
import { getRestriction } from '@mews-commander/legacy-entities/src/ducks/restrictions';
import { getTimeUnitByServiceId } from '@mews-commander/legacy-entities/src/ducks/services';
import { createEntityFormattedMetaSelector } from '@mews-commander/legacy-entities/src/selectors/form';
import { getCurrentEnterpriseIanaTimeZoneIdentifier } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { getCurrentDateTimeLocalizationInfo } from '@mews-commander/core/legacy-globalization';
import {
    FORM_DATA_REFETCH_PERIOD,
    INITIAL_NEW_RESTRICTION_FORM_DAY_VALUES,
    INITIAL_NEW_RESTRICTION_FORM_VALUES,
} from '../constants';
import { RestrictionFormValues } from '../types';
import { convertRestrictionDataToEditFormValues } from '../utils';
import { EditFormValues } from '../components/form/types';
import { getRestrictionsModule } from './common';

const getForm = createSelector(getRestrictionsModule, get('form'));

export const getCurrentRestrictionId = createSelector(getForm, get('currentRestrictionId'));
export const getCurrentRestriction = composeProps(getRestriction, createPropsExtender('id', getCurrentRestrictionId));

export const getCurrentRestrictionFormattedMeta = createEntityFormattedMetaSelector(getCurrentRestriction);
export const getCurrentRestrictionExternalIdentifier = createSelector(getCurrentRestriction, get('externalIdentifier'));
export const getFormType = createSelector(getForm, (form) => form?.formType);
const getLastFetchedEntitiesTimestamp = createSelector(getForm, get('lastFetchedEntitiesTimestamp'));

export const shouldFetchFormRelatedEntities = createSelector(
    getLastFetchedEntitiesTimestamp,
    getNumberProp('now'),
    (lastFetchedEntitiesTimestamp, now) =>
        isNil(lastFetchedEntitiesTimestamp) || now - lastFetchedEntitiesTimestamp > FORM_DATA_REFETCH_PERIOD
);

const getFormMeta = createSelector(getForm, get('meta'));
export const isFormInitializing = createSelector(getFormMeta, get('isFetching'));
export const hasFormFailed = createSelector(getFormMeta, get('hasFailed'));

export const getRestrictionInitialValues = createSelector(getTimeUnitByServiceId, (serviceTimeUnit) =>
    serviceTimeUnit === TimeUnit.Day || serviceTimeUnit === TimeUnit.Hour
        ? INITIAL_NEW_RESTRICTION_FORM_DAY_VALUES
        : INITIAL_NEW_RESTRICTION_FORM_VALUES
);

export const getRestrictionEditFormInitialValues = createSelector(
    getRestriction,
    getRestrictionInitialValues,
    getCurrentEnterpriseIanaTimeZoneIdentifier,
    getTimeUnitByServiceId,
    getStringProp('id'),
    getContainer,
    getCurrentDateTimeLocalizationInfo,
    (restriction, initialValues, timezone: string, timeUnit, id, container, localizationInfo): EditFormValues =>
        restriction
            ? convertRestrictionDataToEditFormValues({
                  restriction,
                  timezone,
                  timeUnit,
                  container,
                  localizationInfo,
              })
            : { formType: 'editForm', multiConditions: false, id, ...initialValues }
);

const restrictionFormValuesSelector = formValuesSelector<RestrictionFormValues>;

export const arePriceExceptionFieldsNil = flow(
    restrictionFormValuesSelector,
    at<RestrictionFormValues>(['maxPrice', 'minPrice']),
    areAllNil
);

export const getDefaultCurrencyOptions = createOptionsSelector(getExchangeRates, {
    label: 'currencyCode',
    value: 'currencyCode',
});
